import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  MdAdminPanelSettings,
  MdBusiness,
  MdCheckCircle,
  MdEmail,
  MdLock,
  MdPerson,
  MdSchedule,
  MdVerifiedUser,
} from 'react-icons/md';

import Card from 'components/card/Card';
import { getProfile, updateProfile } from 'services/authService';
import { ROLE_DISPLAY_NAMES, USER_ROLES } from 'constants/roles';
import { formatDateTime } from 'utils/format';

function DetailRow({ icon, label, value, badgeColor }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const iconBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');

  return (
    <Flex align='center' gap='12px'>
      <Flex
        align='center'
        justify='center'
        w='38px'
        h='38px'
        borderRadius='10px'
        bg={iconBg}
        flexShrink={0}
      >
        <Icon as={icon} color='brand.500' boxSize='20px' />
      </Flex>
      <Box minW='0'>
        <Text color={mutedText} fontSize='xs' fontWeight='600'>
          {label}
        </Text>
        {badgeColor ? (
          <Badge colorScheme={badgeColor} mt='3px'>
            {value || 'N/A'}
          </Badge>
        ) : (
          <Text color={textColor} fontSize='sm' fontWeight='700' noOfLines={2}>
            {value || 'N/A'}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

export default function Profile() {
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const cardBorder = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const avatarBg = useColorModeValue('brand.100', 'whiteAlpha.200');

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const roleLabel = ROLE_DISPLAY_NAMES[user?.role] || user?.role || 'User';
  const isCenterCoordinator = user?.role === USER_ROLES.CENTER_ADMIN;

  const initials = useMemo(() => {
    const name = user?.name || user?.email || 'U';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }, [user]);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      const result = await getProfile();

      if (result.success && result.data) {
        setUser(result.data);
        setProfileForm({
          name: result.data.name || '',
          email: result.data.email || '',
        });
      } else {
        setError(result.error || 'Failed to load profile');
      }

      setLoading(false);
    };

    loadProfile();
  }, []);

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setSavingProfile(true);

    const result = await updateProfile({
      name: profileForm.name.trim(),
      email: profileForm.email.trim(),
    });

    if (result.success && result.data) {
      setUser(result.data);
      setProfileForm({
        name: result.data.name || '',
        email: result.data.email || '',
      });
      toast({
        title: 'Profile updated',
        description: 'Your account details were saved.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Update failed',
        description: result.error || 'Could not update your profile.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }

    setSavingProfile(false);
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast({
        title: 'Passwords do not match',
        description: 'Confirm password must match the new password.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSavingPassword(true);
    const result = await updateProfile({
      current_password: passwordForm.current_password,
      new_password: passwordForm.new_password,
    });

    if (result.success && result.data) {
      setUser(result.data);
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      toast({
        title: 'Password changed',
        description: 'Your password was updated successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Password update failed',
        description: result.error || 'Could not change your password.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }

    setSavingPassword(false);
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Flex minH='420px' align='center' justify='center'>
          <Spinner size='xl' color='brand.500' />
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify='space-between' align={{ base: 'flex-start', md: 'center' }} gap='16px' mb='22px'>
        <Box>
          <Text color={textColor} fontSize='2xl' fontWeight='700'>
            Profile
          </Text>
          <Text color={mutedText} fontSize='sm' mt='4px'>
            Manage your account details and sign-in password.
          </Text>
        </Box>
      </Flex>

      {error && (
        <Alert status='error' borderRadius='12px' mb='20px'>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Grid templateColumns={{ base: '1fr', xl: '360px 1fr' }} gap='20px' alignItems='start'>
        <GridItem>
          <Card p='24px'>
            <Flex direction='column' align='center' textAlign='center'>
              <Avatar
                name={user?.name}
                bg={avatarBg}
                color='brand.500'
                size='xl'
                fontWeight='800'
                mb='16px'
              >
                {initials}
              </Avatar>
              <Text color={textColor} fontSize='xl' fontWeight='800'>
                {user?.name || 'Unnamed user'}
              </Text>
              <Text color={mutedText} fontSize='sm'>
                {user?.email}
              </Text>
              <HStack spacing='8px' mt='14px'>
                <Badge colorScheme={user?.role === USER_ROLES.ADMIN ? 'purple' : 'blue'}>
                  {roleLabel}
                </Badge>
                <Badge colorScheme={user?.is_active === false ? 'red' : 'green'}>
                  {user?.is_active === false ? 'Inactive' : 'Active'}
                </Badge>
              </HStack>
            </Flex>

            <Divider my='22px' borderColor={cardBorder} />

            <SimpleGrid columns={1} gap='18px'>
              <DetailRow icon={MdVerifiedUser} label='Role' value={roleLabel} />
              <DetailRow
                icon={MdBusiness}
                label={isCenterCoordinator ? 'Assigned center' : 'Center'}
                value={user?.center || (user?.role === USER_ROLES.ADMIN ? 'All centers' : 'Not assigned')}
              />
              <DetailRow
                icon={MdSchedule}
                label='Last access'
                value={user?.last_access ? formatDateTime(user.last_access) : 'No recent login recorded'}
              />
              <DetailRow
                icon={MdCheckCircle}
                label='Account created'
                value={user?.account_creation ? formatDateTime(user.account_creation) : 'N/A'}
              />
            </SimpleGrid>
          </Card>
        </GridItem>

        <GridItem>
          <SimpleGrid columns={1} gap='20px'>
            <Card p='24px'>
              <Flex align='center' gap='10px' mb='20px'>
                <Icon as={MdPerson} color='brand.500' boxSize='22px' />
                <Box>
                  <Text color={textColor} fontSize='lg' fontWeight='800'>
                    Account Information
                  </Text>
                  <Text color={mutedText} fontSize='sm'>
                    Update your personal contact details.
                  </Text>
                </Box>
              </Flex>

              <Box as='form' onSubmit={handleProfileSubmit}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='16px'>
                  <FormControl isRequired>
                    <FormLabel color={textColor} fontSize='sm' fontWeight='700'>
                      Full name
                    </FormLabel>
                    <Input
                      bg={inputBg}
                      value={profileForm.name}
                      onChange={(event) => setProfileForm(prev => ({ ...prev, name: event.target.value }))}
                      placeholder='Your full name'
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={textColor} fontSize='sm' fontWeight='700'>
                      Email address
                    </FormLabel>
                    <Input
                      bg={inputBg}
                      type='email'
                      value={profileForm.email}
                      onChange={(event) => setProfileForm(prev => ({ ...prev, email: event.target.value }))}
                      placeholder='you@example.com'
                    />
                  </FormControl>
                </SimpleGrid>

                <Alert status='info' borderRadius='12px' mt='18px'>
                  <AlertIcon />
                  <Text fontSize='sm'>
                    Role and center assignment are managed by system admins.
                  </Text>
                </Alert>

                <Flex justify='flex-end' mt='20px'>
                  <Button colorScheme='brand' type='submit' isLoading={savingProfile} loadingText='Saving'>
                    Save profile
                  </Button>
                </Flex>
              </Box>
            </Card>

            <Card p='24px'>
              <Flex align='center' gap='10px' mb='20px'>
                <Icon as={MdLock} color='brand.500' boxSize='22px' />
                <Box>
                  <Text color={textColor} fontSize='lg' fontWeight='800'>
                    Password
                  </Text>
                  <Text color={mutedText} fontSize='sm'>
                    Change your sign-in password.
                  </Text>
                </Box>
              </Flex>

              <Box as='form' onSubmit={handlePasswordSubmit}>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap='16px'>
                  <FormControl isRequired>
                    <FormLabel color={textColor} fontSize='sm' fontWeight='700'>
                      Current password
                    </FormLabel>
                    <Input
                      bg={inputBg}
                      type='password'
                      value={passwordForm.current_password}
                      onChange={(event) => setPasswordForm(prev => ({ ...prev, current_password: event.target.value }))}
                      placeholder='Current password'
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={textColor} fontSize='sm' fontWeight='700'>
                      New password
                    </FormLabel>
                    <Input
                      bg={inputBg}
                      type='password'
                      value={passwordForm.new_password}
                      onChange={(event) => setPasswordForm(prev => ({ ...prev, new_password: event.target.value }))}
                      placeholder='At least 8 characters'
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={textColor} fontSize='sm' fontWeight='700'>
                      Confirm password
                    </FormLabel>
                    <Input
                      bg={inputBg}
                      type='password'
                      value={passwordForm.confirm_password}
                      onChange={(event) => setPasswordForm(prev => ({ ...prev, confirm_password: event.target.value }))}
                      placeholder='Repeat new password'
                    />
                  </FormControl>
                </SimpleGrid>

                <Flex justify='flex-end' mt='20px'>
                  <Button colorScheme='brand' type='submit' isLoading={savingPassword} loadingText='Updating'>
                    Update password
                  </Button>
                </Flex>
              </Box>
            </Card>

            <Card p='24px'>
              <Flex align='center' gap='10px' mb='16px'>
                <Icon as={user?.role === USER_ROLES.ADMIN ? MdAdminPanelSettings : MdBusiness} color='brand.500' boxSize='22px' />
                <Text color={textColor} fontSize='lg' fontWeight='800'>
                  Access Summary
                </Text>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 3 }} gap='14px'>
                <DetailRow icon={MdAdminPanelSettings} label='Dashboard access' value={user?.role === USER_ROLES.ADMIN ? 'System admin' : 'Center admin'} />
                <DetailRow icon={MdBusiness} label='Scope' value={user?.role === USER_ROLES.ADMIN ? 'All centers' : user?.center || 'Assigned center'} />
                <DetailRow icon={MdEmail} label='Notification email' value={user?.email} />
              </SimpleGrid>
            </Card>
          </SimpleGrid>
        </GridItem>
      </Grid>
    </Box>
  );
}
